// Usage admin route — /admin/usage
//
// Top: 4 KPI cards for "today" (UTC) — requests, tokens, error rate, p50.
// Mid: 30-day daily bar chart (recharts) reading the same summary endpoint.
// Below: filter row (from/to/consumer/model/status) + a paginated rows
// table. Row click reveals the error_message in a modal. Two breakdown
// tables (by consumer / by model) live behind a Tabs switch at the bottom.
//
// Colors: recharts elements read CSS variables via `rgb(var(--primary))`
// instead of hard-coded hex so dark mode flips correctly.

import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts"
import {
  Badge,
  Button,
  FormRow,
  Input,
  Select,
  StatCard,
  Table,
  Tabs,
} from "@/admin-design/_common"
import {
  llmApi,
  type UsageRow,
  type UsageSummaryBucket,
} from "@/api/llm"

const CHART_AXIS_COLOR = "rgb(var(--muted-foreground))"
const CHART_GRID_COLOR = "rgb(var(--border))"
const CHART_TOOLTIP_BG = "rgb(var(--card))"
const CHART_TOOLTIP_BORDER = "rgb(var(--border))"
const CHART_TOOLTIP_FG = "rgb(var(--card-foreground))"

// Cycle through theme-friendly semantic tokens for the per-consumer stack.
// admin-design doesn't (yet) ship dedicated --chart-N tokens, so we lean on
// the existing semantic palette — these all flip correctly in dark mode.
const CHART_SERIES_COLORS = [
  "rgb(var(--primary))",
  "rgb(var(--success))",
  "rgb(var(--warning))",
  "rgb(var(--info))",
  "rgb(var(--destructive))",
  "rgb(var(--accent-foreground))",
]

function todayUTCStart(): string {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString()
}

function thirtyDaysAgoUTC(): string {
  const now = new Date()
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 29))
  return d.toISOString()
}

function fmtNumber(n: number): string {
  return n.toLocaleString()
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

interface Filters {
  from: string
  to: string
  consumerId: string
  model: string
  status: string
}

const EMPTY_FILTERS: Filters = {
  from: "",
  to: "",
  consumerId: "",
  model: "",
  status: "",
}

export default function Usage() {
  const consumers = useQuery({ queryKey: ["admin", "consumers"], queryFn: llmApi.listConsumers })

  // Today metrics (4 cards): summary scoped to today 00:00 UTC → now.
  const todayFrom = todayUTCStart()
  const todaySummary = useQuery({
    queryKey: ["admin", "usage", "summary", "today", todayFrom],
    queryFn: () => llmApi.summary({ from: todayFrom }),
  })

  // 30-day chart: daily buckets across the last 30 days.
  const monthFrom = thirtyDaysAgoUTC()
  const monthSummary = useQuery({
    queryKey: ["admin", "usage", "summary", "30d", monthFrom],
    queryFn: () => llmApi.summary({ from: monthFrom }),
  })

  // Filters drive the rows table + the breakdown summary.
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [drawerRow, setDrawerRow] = useState<UsageRow | null>(null)
  const [breakdownTab, setBreakdownTab] = useState<"by_consumer" | "by_model">("by_consumer")

  const usageList = useQuery({
    queryKey: ["admin", "usage", "list", filters, page],
    queryFn: () =>
      llmApi.listUsage({
        from: filters.from || undefined,
        to: filters.to || undefined,
        consumer: filters.consumerId || undefined,
        model: filters.model || undefined,
        status: filters.status || undefined,
        page,
      }),
  })

  const filteredSummary = useQuery({
    queryKey: ["admin", "usage", "summary", "filtered", filters.from, filters.to],
    queryFn: () =>
      llmApi.summary({
        from: filters.from || undefined,
        to: filters.to || undefined,
      }),
  })

  const consumerOptions = useMemo(() => {
    const opts = [{ label: "All consumers", value: "" }]
    if (consumers.data) {
      for (const c of consumers.data) {
        opts.push({ label: c.name, value: String(c.id) })
      }
    }
    return opts
  }, [consumers.data])

  // No global models catalog anymore — and consumer keys no longer carry a
  // bound remote_model. Derive the filter options from the model_name values
  // observed in usage_logs (server returns these in summary.by_model), deduped
  // and sorted by request volume.
  const modelOptions = useMemo(() => {
    const opts = [{ label: "All models", value: "" }]
    const seen = new Set<string>()
    const byModel = filteredSummary.data?.by_model ?? []
    for (const b of byModel) {
      if (!b.bucket || seen.has(b.bucket)) continue
      seen.add(b.bucket)
      opts.push({ label: b.bucket, value: b.bucket })
    }
    return opts
  }, [filteredSummary.data])

  const statusOptions = [
    { label: "All statuses", value: "" },
    { label: "2xx", value: "2xx" },
    { label: "4xx", value: "4xx" },
    { label: "5xx", value: "5xx" },
  ]

  const todayTotals = todaySummary.data?.totals
  const todayTokens = todayTotals ? todayTotals.prompt_tokens + todayTotals.completion_tokens : 0
  const errorRate =
    todayTotals && todayTotals.requests > 0
      ? (todayTotals.error_count / todayTotals.requests) * 100
      : 0

  // Pivot the server's long-form daily_by_consumer ([bucket, consumer_name,
  // requests, total_tokens]) to wide form keyed by bucket — one column per
  // consumer_name. The result feeds a recharts BarChart with one Bar per
  // consumer all sharing the same stackId.
  const { dailyChartData, dailyChartKeys } = useMemo(() => {
    if (!monthSummary.data) return { dailyChartData: [], dailyChartKeys: [] as string[] }
    const byBucket = new Map<string, Record<string, number | string>>()
    const consumerNames = new Set<string>()
    for (const row of monthSummary.data.daily_by_consumer ?? []) {
      consumerNames.add(row.consumer_name)
      const existing = byBucket.get(row.bucket) ?? { bucket: row.bucket }
      existing[row.consumer_name] = row.requests
      byBucket.set(row.bucket, existing)
    }
    // Buckets sorted ascending so the stacked bars line up chronologically.
    const data = Array.from(byBucket.values()).sort((a, b) =>
      String(a.bucket).localeCompare(String(b.bucket)),
    )
    // Total volume per consumer drives series order — busiest at the bottom
    // of the stack, matching legend order, and gives stable color assignment
    // across renders for the same dataset.
    const totals = new Map<string, number>()
    for (const name of consumerNames) totals.set(name, 0)
    for (const bucket of data) {
      for (const name of consumerNames) {
        totals.set(name, (totals.get(name) ?? 0) + Number(bucket[name] ?? 0))
      }
    }
    const keys = Array.from(consumerNames).sort(
      (a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0),
    )
    return { dailyChartData: data, dailyChartKeys: keys }
  }, [monthSummary.data])

  const consumerNameById = useMemo(() => {
    const m = new Map<string, string>()
    if (consumers.data) {
      for (const c of consumers.data) m.set(String(c.id), c.name)
    }
    return m
  }, [consumers.data])

  const onResetFilters = () => {
    setFilters(EMPTY_FILTERS)
    setPage(1)
  }

  const breakdownData =
    breakdownTab === "by_consumer"
      ? filteredSummary.data?.by_consumer
      : filteredSummary.data?.by_model

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground">Usage</h1>
        <span className="text-xs text-muted-foreground">All times UTC</span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Today requests"
          value={fmtNumber(todayTotals?.requests ?? 0)}
          loading={todaySummary.isLoading}
        />
        <StatCard
          label="Today tokens"
          value={fmtNumber(todayTokens)}
          hint="prompt + completion"
          loading={todaySummary.isLoading}
        />
        <StatCard
          label="Error rate"
          value={`${errorRate.toFixed(1)}%`}
          tone={errorRate > 5 ? "destructive" : errorRate > 1 ? "warning" : undefined}
          hint={
            todayTotals
              ? `${fmtNumber(todayTotals.error_count)} / ${fmtNumber(todayTotals.requests)}`
              : undefined
          }
          loading={todaySummary.isLoading}
        />
        <StatCard
          label="p50 latency"
          value={`${fmtNumber(todayTotals?.p50_latency_ms ?? 0)} ms`}
          hint={todayTotals ? `p95 ${fmtNumber(todayTotals.p95_latency_ms)} ms` : undefined}
          loading={todaySummary.isLoading}
        />
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">Last 30 days</h2>
        <div className="h-64">
          {monthSummary.isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : dailyChartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No data
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyChartData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid stroke={CHART_GRID_COLOR} strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  stroke={CHART_AXIS_COLOR}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: CHART_GRID_COLOR }}
                />
                <YAxis
                  stroke={CHART_AXIS_COLOR}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: CHART_GRID_COLOR }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: CHART_TOOLTIP_BG,
                    border: `1px solid ${CHART_TOOLTIP_BORDER}`,
                    borderRadius: 6,
                    color: CHART_TOOLTIP_FG,
                    fontSize: 12,
                  }}
                  cursor={{ fill: "rgb(var(--muted))" }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: CHART_TOOLTIP_FG }} />
                {/* One Bar per consumer; identical stackId stacks them per
                    bucket. The last series in render order gets the rounded
                    top corners, so reverse-iterate by total volume to put the
                    busiest series on the bottom. */}
                {dailyChartKeys.map((name, i) => (
                  <Bar
                    key={name}
                    dataKey={name}
                    stackId="requests"
                    fill={CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length]}
                    radius={i === dailyChartKeys.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">Filters</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
          <FormRow label="From" stacked>
            <Input
              type="date"
              value={filters.from}
              onChange={(e) => {
                setFilters({ ...filters, from: e.target.value })
                setPage(1)
              }}
            />
          </FormRow>
          <FormRow label="To" stacked>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => {
                setFilters({ ...filters, to: e.target.value })
                setPage(1)
              }}
            />
          </FormRow>
          <FormRow label="Consumer" stacked>
            <Select
              value={filters.consumerId}
              onValueChange={(v) => {
                setFilters({ ...filters, consumerId: v })
                setPage(1)
              }}
              options={consumerOptions}
            />
          </FormRow>
          <FormRow label="Model" stacked>
            <Select
              value={filters.model}
              onValueChange={(v) => {
                setFilters({ ...filters, model: v })
                setPage(1)
              }}
              options={modelOptions}
            />
          </FormRow>
          <FormRow label="Status" stacked>
            <Select
              value={filters.status}
              onValueChange={(v) => {
                setFilters({ ...filters, status: v })
                setPage(1)
              }}
              options={statusOptions}
            />
          </FormRow>
        </div>
        <div className="mt-3 flex justify-end">
          <Button variant="secondary" size="sm" onClick={onResetFilters}>
            Reset
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">Recent requests</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="xs"
              disabled={page <= 1 || usageList.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">page {page}</span>
            <Button
              variant="secondary"
              size="xs"
              disabled={
                usageList.isFetching ||
                !usageList.data ||
                usageList.data.rows.length < (usageList.data.page_size ?? 50)
              }
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
        <Table>
          <Table.Head>
            <Table.Row interactive={false}>
              <Table.Header>Started</Table.Header>
              <Table.Header>Consumer</Table.Header>
              <Table.Header>Model</Table.Header>
              <Table.Header>Status</Table.Header>
              <Table.Header className="text-right">Tokens</Table.Header>
              <Table.Header className="text-right">Latency</Table.Header>
              <Table.Header>Notes</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {usageList.isLoading && (
              <Table.Row interactive={false}>
                <Table.Cell colSpan={7} className="text-center text-muted-foreground">
                  Loading…
                </Table.Cell>
              </Table.Row>
            )}
            {usageList.data && usageList.data.rows.length === 0 && (
              <Table.Empty
                colSpan={7}
                title="No requests in this window"
                description="Adjust the filters or wait for traffic."
              />
            )}
            {usageList.data?.rows.map((r) => {
              const total = r.total_tokens ?? (r.prompt_tokens ?? 0) + (r.completion_tokens ?? 0)
              const isError = r.status_code >= 400
              return (
                <Table.Row key={r.id} onClick={() => setDrawerRow(r)} className="cursor-pointer">
                  <Table.Cell className="whitespace-nowrap text-muted-foreground">
                    {fmtDateTime(r.started_at)}
                  </Table.Cell>
                  <Table.Cell>{r.consumer_name || "—"}</Table.Cell>
                  <Table.Cell>
                    <code className="text-xs text-muted-foreground">{r.model_name}</code>
                  </Table.Cell>
                  <Table.Cell>
                    {isError ? (
                      <Badge variant="destructive">{r.status_code}</Badge>
                    ) : (
                      <Badge variant="success">{r.status_code}</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular-nums">
                    {fmtNumber(total)}
                    {r.tokens_estimated && <span className="ml-1 text-xs text-muted-foreground">est</span>}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular-nums">{fmtNumber(r.latency_ms)} ms</Table.Cell>
                  <Table.Cell>
                    {r.error_code ? (
                      <span className="text-xs text-destructive">{r.error_code}</span>
                    ) : r.streamed ? (
                      <span className="text-xs text-muted-foreground">streamed</span>
                    ) : (
                      ""
                    )}
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="px-4 pt-3">
          <Tabs
            aria-label="Usage breakdown"
            value={breakdownTab}
            onChange={(k) => setBreakdownTab(k as "by_consumer" | "by_model")}
            items={[
              { key: "by_consumer", label: "By consumer" },
              { key: "by_model", label: "By model" },
            ]}
          />
        </div>
        <BreakdownTable
          rows={breakdownData ?? []}
          headerLabel={breakdownTab === "by_consumer" ? "Consumer" : "Model"}
          consumerNameById={consumerNameById}
          isConsumer={breakdownTab === "by_consumer"}
          loading={filteredSummary.isLoading}
        />
      </div>

      {drawerRow && (
        <RowDetailModal row={drawerRow} onClose={() => setDrawerRow(null)} />
      )}
    </div>
  )
}

interface BreakdownTableProps {
  rows: UsageSummaryBucket[]
  headerLabel: string
  consumerNameById: Map<string, string>
  isConsumer: boolean
  loading: boolean
}

function BreakdownTable({ rows, headerLabel, consumerNameById, isConsumer, loading }: BreakdownTableProps) {
  return (
    <Table>
      <Table.Head>
        <Table.Row interactive={false}>
          <Table.Header>{headerLabel}</Table.Header>
          <Table.Header className="text-right">Requests</Table.Header>
          <Table.Header className="text-right">Tokens</Table.Header>
          <Table.Header className="text-right">Errors</Table.Header>
          <Table.Header className="text-right">Avg latency</Table.Header>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {loading && (
          <Table.Row interactive={false}>
            <Table.Cell colSpan={5} className="text-center text-muted-foreground">
              Loading…
            </Table.Cell>
          </Table.Row>
        )}
        {!loading && rows.length === 0 && (
          <Table.Empty colSpan={5} title="No activity" description="The current filter window is empty." />
        )}
        {rows.map((r) => {
          const label = isConsumer ? consumerNameById.get(r.bucket) ?? r.bucket : r.bucket
          const total = r.prompt_tokens + r.completion_tokens
          return (
            <Table.Row key={r.bucket} interactive={false}>
              <Table.Cell className="font-medium">{label}</Table.Cell>
              <Table.Cell className="text-right tabular-nums">{r.requests.toLocaleString()}</Table.Cell>
              <Table.Cell className="text-right tabular-nums">{total.toLocaleString()}</Table.Cell>
              <Table.Cell className="text-right tabular-nums">
                {r.error_count > 0 ? (
                  <span className="text-destructive">{r.error_count.toLocaleString()}</span>
                ) : (
                  r.error_count
                )}
              </Table.Cell>
              <Table.Cell className="text-right tabular-nums">{r.avg_latency_ms.toLocaleString()} ms</Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table>
  )
}

function RowDetailModal({ row, onClose }: { row: UsageRow; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          className="w-full max-w-xl rounded-lg border border-border bg-card shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-card-foreground">Request detail</h2>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{row.request_id}</p>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 px-6 py-4 text-sm">
            <dt className="text-muted-foreground">Started</dt>
            <dd className="text-foreground">{fmtDateTime(row.started_at)}</dd>
            <dt className="text-muted-foreground">Ended</dt>
            <dd className="text-foreground">{fmtDateTime(row.ended_at)}</dd>
            <dt className="text-muted-foreground">Consumer</dt>
            <dd className="text-foreground">{row.consumer_name || "—"}</dd>
            <dt className="text-muted-foreground">Model</dt>
            <dd className="text-foreground">{row.model_name}</dd>
            <dt className="text-muted-foreground">Provider</dt>
            <dd className="text-foreground">{row.provider_name ?? "—"}</dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd className="text-foreground">{row.status_code}</dd>
            <dt className="text-muted-foreground">Prompt / completion</dt>
            <dd className="tabular-nums text-foreground">
              {fmtNumber(row.prompt_tokens ?? 0)} / {fmtNumber(row.completion_tokens ?? 0)}
              {row.tokens_estimated && <span className="ml-2 text-xs text-muted-foreground">(estimated)</span>}
            </dd>
            <dt className="text-muted-foreground">Latency</dt>
            <dd className="tabular-nums text-foreground">{fmtNumber(row.latency_ms)} ms</dd>
            <dt className="text-muted-foreground">Streamed</dt>
            <dd className="text-foreground">{row.streamed ? "yes" : "no"}</dd>
          </dl>
          {(row.error_code || row.error_message) && (
            <div className="mx-6 mb-4 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2 text-sm text-destructive-soft-foreground">
              {row.error_code && <div className="font-mono text-xs">{row.error_code}</div>}
              {row.error_message && (
                <div className="mt-1 whitespace-pre-wrap break-words">{row.error_message}</div>
              )}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
            <Button size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
